import { EventTime } from './event.time';
import { RecurrenceError, RecurrenceErrorCodes } from './recurrence.errors';

export type FrequencyKind =
  | 'event'
  | 'custom'
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'last';

export const ALL_FREQUENCY_KINDS: ReadonlyArray<FrequencyKind> = Object.freeze([
  'event',
  'custom',
  'daily',
  'weekly',
  'biweekly',
  'monthly',
  'last',
]);

export interface RecurrenceRange {
  readonly startDateTime: string;
  readonly endDateTime: string;
}

export interface RecurrenceRule {
  readonly kind: FrequencyKind;
  readonly range: RecurrenceRange;
  readonly timezone: string;
  readonly isAllDay: boolean;
  readonly isEndAllDay: boolean;
  readonly explicit?: ReadonlyArray<EventTime>;
}

// Frozen so generators always receive the original (start,end) — mutation after filtering is the root of DXP-3325.
export function createRecurrenceRule(input: {
  kind: FrequencyKind;
  range: RecurrenceRange;
  timezone: string;
  isAllDay?: boolean;
  isEndAllDay?: boolean;
  explicit?: ReadonlyArray<EventTime>;
}): RecurrenceRule {
  if (!ALL_FREQUENCY_KINDS.includes(input.kind)) {
    throw new RecurrenceError(`Unknown frequency kind: ${input.kind}`, RecurrenceErrorCodes.INVALID_KIND);
  }

  if (!input.range || !input.range.startDateTime) {
    throw new RecurrenceError('Range.startDateTime is required', RecurrenceErrorCodes.INVALID_RANGE);
  }

  // For non-event kinds we require an end >= start.
  if (input.kind !== 'event' && input.kind !== 'custom') {
    if (!input.range.endDateTime) {
      throw new RecurrenceError(
        `Range.endDateTime is required for kind=${input.kind}`,
        RecurrenceErrorCodes.INVALID_RANGE,
      );
    }
    if (new Date(input.range.endDateTime).getTime() < new Date(input.range.startDateTime).getTime()) {
      throw new RecurrenceError(
        'Range end must be on or after range start',
        RecurrenceErrorCodes.INVALID_RANGE,
      );
    }
  }

  if (input.kind === 'custom' && (!input.explicit || input.explicit.length === 0)) {
    throw new RecurrenceError(
      'kind=custom requires non-empty explicit[]',
      RecurrenceErrorCodes.MISSING_EXPLICIT,
    );
  }

  const range: RecurrenceRange = Object.freeze({
    startDateTime: input.range.startDateTime,
    endDateTime: input.range.endDateTime,
  });

  const explicit = input.explicit
    ? Object.freeze(input.explicit.map(t => Object.freeze({ ...t })))
    : undefined;

  return Object.freeze({
    kind: input.kind,
    range,
    timezone: input.timezone || 'America/New_York',
    isAllDay: !!input.isAllDay,
    isEndAllDay: !!input.isEndAllDay,
    explicit,
  });
}

export function withRule(
  rule: RecurrenceRule,
  patch: Partial<{
    kind: FrequencyKind;
    range: Partial<RecurrenceRange>;
    timezone: string;
    isAllDay: boolean;
    isEndAllDay: boolean;
    explicit: ReadonlyArray<EventTime>;
  }>,
): RecurrenceRule {
  return createRecurrenceRule({
    kind: patch.kind ?? rule.kind,
    range: {
      startDateTime: patch.range?.startDateTime ?? rule.range.startDateTime,
      endDateTime: patch.range?.endDateTime ?? rule.range.endDateTime,
    },
    timezone: patch.timezone ?? rule.timezone,
    isAllDay: patch.isAllDay ?? rule.isAllDay,
    isEndAllDay: patch.isEndAllDay ?? rule.isEndAllDay,
    explicit: patch.explicit ?? rule.explicit,
  });
}
